import { getDocumentFirstFileId } from 'libs/core/hooks/src/Document/useDocumentFirstFile';
import React, { createContext, useCallback, useContext, useState } from 'react';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import {
	useLastDocument,
	usePersonCommunicationData,
	useProcessControl,
	useRezaSwitcher,
} from '@gov-nx/core/hooks';
import {
	useCountry,
	useLanguage,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import { useAutorizaceStore } from '@gov-nx/store';
import {
	getApiWarningRequestHeader,
	WarningRequestHeader,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	FormData,
	ServiceContextControls,
	ServiceContextTypes,
} from './service.types';

export const ServiceContext = createContext<ServiceContextTypes | null>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function VypisZRejstrikuTrestuFOContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { filteredEuCountriesToSelect } = useCountry({
		filterOut: ['cz'],
	});
	const { filteredLanguagesToSelect } = useLanguage({
		filterOut: ['CS'],
	});
	const rezaSwitcher = useRezaSwitcher({ code });
	const { toastMessageError } = useMessageEvents();
	const [changeEmail, setChangeEmail] = useState<boolean>(false);
	const { emailVerified } = usePersonCommunicationData();
	const { downloadDocument } = useDownloadEvents();
	const { controls, setControls } = useProcessControl<ServiceContextControls>({
		mustWaitForProcessing: null,
	});
	const authContext = useAutorizaceContext();
	const getServiceAuthorizationType = useAutorizaceStore(
		'getServiceAuthorizationType'
	);
	const AUTHORIZATION_SERVICE_NAME = 'REJSTRIK_TRESTU_VYPIS_EGSB_2';
	const requiresConsent =
		getServiceAuthorizationType(AUTHORIZATION_SERVICE_NAME) === 'NIZKA';

	const formDefinition = FormInstance({
		code,
		isEmail: !!emailVerified,
		email: emailVerified,
		requiresConsent,
	});
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const lastDocument = useLastDocument(code, true, { typOsoby: 'FO' });
	const { axiosInstance } = useAxiosInstance();

	const submitMutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: (data: FormData) => {
			const prepared = prepareSubmitData(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error: Error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (response) => {
			const nonHighAuthorizationFlow = async () => {
				const headerWarning = getApiWarningRequestHeader(response);
				if (headerWarning) {
					lastDocument.invalidateCache();
					formDefinition.formReset();
					setControls({
						mustWaitForProcessing: headerWarning,
						processLoading: false,
					});
				} else if (response.data > 0) {
					const souborId = await getDocumentFirstFileId(axiosInstance, {
						documentId: response.data,
						onError: () =>
							toastMessageError(tsn('formular.zprava.chyba-pri-odesilani')),
					});

					if (!souborId) {
						return;
					}

					downloadDocument({
						documentId: response.data,
						souborId,
						callback: async () => {
							lastDocument.invalidateCache();
							formDefinition.formReset();
							setControls({ processLoading: false });
						},
					});
				} else if (response.data === 0) {
					lastDocument.invalidateCache();
					formDefinition.formReset();
					setControls({
						mustWaitForProcessing: WarningRequestHeader.UNKNOW,
						processLoading: false,
					});
				} else {
					toastMessageError(tsn('formular.zprava.chyba-pri-odesilani'));
					lastDocument.invalidateCache();
					setControls({
						processLoading: false,
						processError: new GovError(
							`[Service]: An unexpected error has occurred in (${code})`
						),
					});
				}
			};

			if (authContext) {
				// For high authorization the OTP dialog takes over, so the inline
				// download flow won't run — clear the loading state so the form
				// isn't stuck if the dialog is dismissed.
				if (
					getServiceAuthorizationType(AUTHORIZATION_SERVICE_NAME) === 'VYSOKA'
				) {
					setControls({ processLoading: false });
				}
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				await nonHighAuthorizationFlow();
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({
			mustWaitForProcessing: null,
			processError: null,
			processLoading: true,
		});
		submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	return (
		<ServiceContext.Provider
			value={{
				code,
				rezaSwitcher,
				formDefinition,
				submitMutation,
				onSubmit,
				controls,
				setControls,
				isEmail: !!emailVerified,
				email: emailVerified,
				changeEmail,
				setChangeEmail,
				lastDocument,
				filteredEuCountriesToSelect,
				filteredLanguagesToSelect,
				requiresConsent,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisRejstrikuTrestuFOContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
